import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Edit2, Plus, Save, ArrowUp, ArrowDown, Trash2, Eye, X } from 'lucide-react';
import { FIELD_TYPES, SECTIONS, FIELD_CONDITION_OPERATORS } from './constants';

export default function FieldsEditor({
  fields, editMode, onStartEdit, onCancel, onSave, onAdd, onRemove, onMove, onUpdate, saving, title, description,
  allFieldNames,
}) {
  const availableFields = allFieldNames || fields.map(f => f.name).filter(Boolean);
  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <div>
          <p className="text-sm text-slate-600 font-medium">{fields.length} {title?.toLowerCase()}</p>
          {description && <p className="text-xs text-slate-400">{description}</p>}
        </div>
        {!editMode ? (
          <Button size="sm" variant="outline" onClick={onStartEdit} data-testid="edit-fields-btn">
            <Edit2 className="h-3.5 w-3.5 mr-1" /> Edit Fields
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={onAdd} data-testid="add-field-btn">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add Field
            </Button>
            <Button size="sm" variant="outline" onClick={onCancel}>Cancel</Button>
            <Button size="sm" onClick={onSave} disabled={saving} className="bg-green-600 hover:bg-green-700 text-white" data-testid="save-fields-btn">
              <Save className="h-3.5 w-3.5 mr-1" /> {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        )}
      </div>

      <div className="h-[400px] overflow-y-auto border rounded-lg">
        <div className="space-y-2 p-2">
          {fields.map((field, idx) => (
            <div key={field._key || field.id || idx} className="p-3 rounded-lg border bg-white" data-testid={`field-row-${idx}`}>
              {editMode ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="flex flex-col gap-0.5">
                      <button onClick={() => onMove(idx, -1)} disabled={idx === 0} className="text-slate-400 hover:text-slate-600 disabled:opacity-30">
                        <ArrowUp className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => onMove(idx, 1)} disabled={idx === fields.length - 1} className="text-slate-400 hover:text-slate-600 disabled:opacity-30">
                        <ArrowDown className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <span className="text-xs text-slate-400 w-6">{idx + 1}</span>
                    <div className="flex-1 flex flex-col gap-2 md:grid md:grid-cols-12 md:items-center">
                      <div className="col-span-3">
                        <Input
                          value={field.label}
                          onChange={(e) => onUpdate(idx, 'label', e.target.value)}
                          placeholder="Label (Display Name)"
                          className="text-sm"
                        />
                      </div>
                      <div className="col-span-2">
                        <Input
                          value={field.name}
                          onChange={(e) => onUpdate(idx, 'name', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
                          placeholder="Field key"
                          className="text-xs font-mono"
                        />
                      </div>
                      <div className="col-span-2">
                        <Select value={field.type} onValueChange={(v) => onUpdate(idx, 'type', v)}>
                          <SelectTrigger className="text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {FIELD_TYPES.map(ft => <SelectItem key={ft.value} value={ft.value}>{ft.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Select value={field.section} onValueChange={(v) => onUpdate(idx, 'section', v)}>
                          <SelectTrigger className="text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {SECTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2 flex items-center gap-2">
                        <label className="flex items-center gap-1 text-xs">
                          <input
                            type="checkbox"
                            checked={field.required || false}
                            onChange={(e) => onUpdate(idx, 'required', e.target.checked)}
                            className="rounded"
                          />
                          Req
                        </label>
                        <label className="flex items-center gap-1 text-xs" title="Auto-fill from order creation">
                          <input
                            type="checkbox"
                            checked={field.auto_filled || false}
                            onChange={(e) => onUpdate(idx, 'auto_filled', e.target.checked)}
                            className="rounded"
                          />
                          Auto
                        </label>
                      </div>
                    </div>
                    <button onClick={() => onRemove(idx)} className="text-red-400 hover:text-red-600">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  
                  {(field.type === 'select' || field.type === 'checkbox_group') && (
                    <div className="ml-10">
                      <Label className="text-[10px] text-slate-500">Options (comma-separated)</Label>
                      <Input
                        value={Array.isArray(field.options) ? field.options.join(', ') : (field.options || '')}
                        onChange={(e) => onUpdate(idx, 'options', e.target.value)}
                        placeholder="Option 1, Option 2, Option 3"
                        className="text-xs"
                      />
                    </div>
                  )}

                  <div className="ml-10">
                    {field.conditional ? (
                      <div className="p-2 bg-amber-50 rounded-lg border border-amber-200 space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-[10px] font-medium text-amber-700 flex items-center gap-1">
                            <Eye className="h-3 w-3" /> Visible when:
                          </p>
                          <button
                            onClick={() => onUpdate(idx, 'conditional', null)}
                            className="text-red-400 hover:text-red-600"
                            title="Remove condition"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                        <div className="flex items-center gap-2">
                          <Select
                            value={field.conditional.field}
                            onValueChange={(v) => onUpdate(idx, 'conditional', { ...field.conditional, field: v })}
                          >
                            <SelectTrigger className="w-[140px] text-xs">
                              <SelectValue placeholder="Select field" />
                            </SelectTrigger>
                            <SelectContent>
                              {availableFields.filter(f => f !== field.name).map(f => (
                                <SelectItem key={f} value={f}>{f}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Select
                            value={field.conditional.show_when || 'in'}
                            onValueChange={(v) => onUpdate(idx, 'conditional', { ...field.conditional, show_when: v })}
                          >
                            <SelectTrigger className="w-[120px] text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {FIELD_CONDITION_OPERATORS.map(op => (
                                <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {field.conditional.show_when !== 'any' && (
                            <Input
                              value={Array.isArray(field.conditional.values) ? field.conditional.values.join(', ') : (field.conditional.values || '')}
                              onChange={(e) => onUpdate(idx, 'conditional', {
                                ...field.conditional,
                                values: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                              })}
                              placeholder="Value1, Value2"
                              className="flex-1 text-xs"
                            />
                          )}
                        </div>
                      </div>
                    ) : (field.type === 'select' || field.type === 'toggle') ? (
                      <button
                        onClick={() => onUpdate(idx, 'conditional', { field: '', values: [], show_when: 'in' })}
                        className="text-[10px] text-amber-600 hover:text-amber-700 flex items-center gap-1 hover:underline"
                      >
                        <Eye className="h-3 w-3" /> Add visibility condition
                      </button>
                    ) : null}
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <span className="text-xs text-slate-400 w-6">{idx + 1}</span>
                  <span className="text-sm font-medium text-slate-700">{field.label}</span>
                  <span className="text-[10px] text-slate-400 font-mono">{field.name}</span>
                  <Badge variant="outline" className="text-[10px]">{field.type}</Badge>
                  <span className="text-[10px] text-slate-400">{field.section}</span>
                  {field.required && <Badge className="text-[10px] bg-red-50 text-red-600">Required</Badge>}
                  {field.auto_filled && <Badge className="text-[10px] bg-blue-50 text-blue-600">Auto-fill</Badge>}
                  {field.options?.length > 0 && (
                    <span className="text-[10px] text-slate-400 truncate max-w-[200px]">[{field.options.join(', ')}]</span>
                  )}
                  {field.conditional && field.conditional.field && (
                    <Badge className="text-[10px] bg-amber-50 text-amber-700">
                      <Eye className="h-2.5 w-2.5 mr-0.5" />
                      {field.conditional.show_when === 'any' ? `if ${field.conditional.field}` :
                       `if ${field.conditional.field} ${field.conditional.show_when === 'in' ? '=' : '≠'} ${(field.conditional.values || []).join(', ')}`}
                    </Badge>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
